// src/pages/Squad.tsx
import { useState, useEffect, useRef } from 'react'
import { Users, Layout, Activity, Award, Plus, MoreHorizontal, Copy, Share2, Check } from 'lucide-react'
import { useSquads } from '../stores/useSquads'

function fmtCode(code: string) {
  if (code.length === 6) return `${code.slice(0, 3)}·${code.slice(3)}`
  return code
}

interface InviteBannerProps {
  code: string;
  squadName: string;
}

function InviteBanner({ code, squadName }: InviteBannerProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleShare = async () => {
    const text = `Join my squad "${squadName}" on Loqin!\n\nUse code: ${code}\n\nDownload Loqin and enter this code in the Squads tab.`
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${squadName} on Loqin`,
          text: text,
        })
      } catch (err) {
        console.warn('Share error:', err)
      }
    } else {
      navigator.clipboard.writeText(text)
      alert('Invite text copied to clipboard!')
    }
  }

  return (
    <div className="invite-banner animate-fade-slide-up" style={{ margin: '0 var(--lg) var(--lg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--sm)' }}>
        <Users size={16} className="text-accent" style={{ marginRight: 'var(--sm)' }} />
        <span className="label text-accent">INVITE YOUR TEAM</span>
      </div>
      <p className="body text-muted" style={{ marginBottom: 'var(--lg)' }}>
        You're the only one here. Share your code and lock in together.
      </p>

      {/* Code display */}
      <div
        className="card card-low"
        style={{
          padding: 'var(--md) var(--lg)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 'var(--md)',
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: '28px',
            letterSpacing: '6px',
            color: 'var(--text)',
            fontWeight: 'bold',
          }}
        >
          {fmtCode(code)}
        </span>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 'var(--sm)' }}>
        <button
          onClick={handleCopy}
          className="btn"
          style={{
            flex: 1,
            backgroundColor: copied ? 'color-mix(in srgb, var(--signal) 14%, transparent)' : 'var(--card-low)',
            color: copied ? 'var(--signal)' : 'var(--text-muted)',
            border: `1px solid ${copied ? 'var(--signal)' : 'var(--border)'}`,
            padding: '10px',
            fontSize: '12px',
          }}
        >
          <Copy size={14} style={{ marginRight: '4px' }} />
          {copied ? 'COPIED!' : 'COPY CODE'}
        </button>

        <button
          onClick={handleShare}
          className="btn btn-primary"
          style={{
            flex: 1,
            padding: '10px',
            fontSize: '12px',
          }}
        >
          <Share2 size={14} style={{ marginRight: '4px' }} />
          SHARE
        </button>
      </div>
    </div>
  )
}

export default function SquadPage() {
  const {
    squads,
    activeSquadId,
    members,
    squadTasks,
    snrLeaderboard,
    memberFocusTasks,
    load,
    createSquad,
    joinSquad,
    leaveSquad,
    createSquadTask,
    completeSquadTask,
    memberInitial,
  } = useSquads()

  const [activeTab, setActiveTab] = useState<'board' | 'activity' | 'leaderboard'>('board')
  const [showCreate, setShowCreate] = useState(false)
  const [showJoin, setShowJoin] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [newSquadName, setNewSquadName] = useState('')
  const [joinCode, setJoinCode] = useState('')
  const [newTaskText, setNewTaskText] = useState('')
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    load()
  }, [load])

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const activeSquad = squads.find((s) => s.id === activeSquadId)
  const isSoleMember = members.length <= 1

  const handleCopyCode = () => {
    if (!activeSquad) return
    navigator.clipboard.writeText(activeSquad.join_code)
    alert(`Code ${fmtCode(activeSquad.join_code)} copied to clipboard!`)
    setShowMenu(false)
  }

  const handleShareLink = async () => {
    if (!activeSquad) return
    const text = `Join my squad "${activeSquad.name}" on Loqin!\n\nUse code: ${activeSquad.join_code}`
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${activeSquad.name}`,
          text: text,
        })
      } catch (err) {
        console.warn('Share error:', err)
      }
    } else {
      navigator.clipboard.writeText(text)
      alert('Squad invite details copied to clipboard!')
    }
    setShowMenu(false)
  }

  const handleLeaveSquad = async () => {
    if (!activeSquad) return
    if (confirm(`Are you sure you want to leave "${activeSquad.name}"? You can rejoin with the same code later.`)) {
      try {
        await leaveSquad(activeSquad.id)
      } catch {
        alert('Could not leave squad. Please try again.')
      }
    }
    setShowMenu(false)
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  if (squads.length === 0 && !showCreate && !showJoin) {
    return (
      <div className="page page-top animate-fade-slide-up" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', textAlign: 'center' }}>
        <Users size={64} className="text-accent" style={{ marginBottom: 'var(--lg)' }} />
        <h1 className="display-md text-primary" style={{ marginBottom: 'var(--sm)' }}>
          Lock in together
        </h1>
        <p className="body text-muted" style={{ marginBottom: 'var(--xxl)', maxWidth: '300px' }}>
          Create a squad and invite your team, or join one with a code.
        </p>
        <button
          onClick={() => setShowCreate(true)}
          className="btn btn-primary"
          style={{ marginBottom: 'var(--md)' }}
        >
          Create Squad
        </button>
        <button
          onClick={() => setShowJoin(true)}
          className="btn btn-outline"
        >
          Join with a code
        </button>
      </div>
    )
  }

  // ── Create flow ────────────────────────────────────────────────────────────
  if (showCreate) {
    return (
      <div className="page page-top animate-fade-slide-up">
        <h1 className="display-md text-primary" style={{ marginBottom: 'var(--xl)' }}>Create Squad</h1>
        <div className="input-wrap">
          <label className="input-label">Squad Name</label>
          <input
            type="text"
            className="input"
            value={newSquadName}
            onChange={(e) => setNewSquadName(e.target.value)}
            placeholder="The Avengers"
          />
        </div>
        <button
          onClick={async () => {
            if (!newSquadName.trim()) return
            try {
              await createSquad(newSquadName.trim())
              setNewSquadName('')
              setShowCreate(false)
            } catch {
              alert('Could not create squad. Please try again.')
            }
          }}
          className="btn btn-primary"
          style={{ marginBottom: 'var(--md)' }}
        >
          Create
        </button>
        <button
          onClick={() => setShowCreate(false)}
          className="btn btn-ghost"
        >
          Cancel
        </button>
      </div>
    )
  }

  // ── Join flow ──────────────────────────────────────────────────────────────
  if (showJoin) {
    return (
      <div className="page page-top animate-fade-slide-up">
        <h1 className="display-md text-primary" style={{ marginBottom: 'var(--xl)' }}>Join Squad</h1>
        <div className="input-wrap">
          <label className="input-label">Join Code</label>
          <input
            type="text"
            className="input"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.replace(/[·\s]/g, '').toUpperCase())}
            placeholder="ABCDEF"
          />
        </div>
        <button
          onClick={async () => {
            if (!joinCode.trim()) return
            try {
              await joinSquad(joinCode.trim())
              setJoinCode('')
              setShowJoin(false)
            } catch {
              alert('No squad found with that code. Check and try again.')
            }
          }}
          className="btn btn-primary"
          style={{ marginBottom: 'var(--md)' }}
        >
          Join
        </button>
        <button
          onClick={() => setShowJoin(false)}
          className="btn btn-ghost"
        >
          Cancel
        </button>
      </div>
    )
  }

  // ── Squad view ─────────────────────────────────────────────────────────────
  return (
    <div className="page page-top animate-fade-slide-up" style={{ paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{ padding: '0 0 var(--lg) 0', marginBottom: 'var(--lg)', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--md)' }}>
          <div style={{ flex: 1, marginRight: 'var(--md)' }}>
            <h1 className="display-md text-primary" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {activeSquad?.name}
            </h1>
            <p className="label text-dim" style={{ fontSize: '10px', marginTop: '2px' }}>
              {members.length} {members.length === 1 ? 'member' : 'members'} · {fmtCode(activeSquad?.join_code ?? '')}
            </p>
          </div>
          <div style={{ position: 'relative' }} ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="btn btn-ghost btn-icon"
              style={{ padding: 'var(--sm)', color: 'var(--text-muted)' }}
            >
              <MoreHorizontal size={22} />
            </button>

            {showMenu && (
              <div
                className="card card-high"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '40px',
                  width: '200px',
                  zIndex: 10,
                  padding: 'var(--xs)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                }}
              >
                <button
                  onClick={handleCopyCode}
                  className="btn btn-ghost btn-sm"
                  style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '10px 12px' }}
                >
                  📋 Copy Invite Code
                </button>
                <button
                  onClick={handleShareLink}
                  className="btn btn-ghost btn-sm"
                  style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '10px 12px' }}
                >
                  🔗 Share Invite Link
                </button>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }}></div>
                <button
                  onClick={handleLeaveSquad}
                  className="btn btn-ghost btn-sm"
                  style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '10px 12px', color: 'var(--noise)' }}
                >
                  🚪 Leave Squad
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="pill-tabs" style={{ marginBottom: 0 }}>
          {(['board', 'activity', 'leaderboard'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pill-tab ${activeTab === tab ? 'active' : ''}`}
            >
              {tab === 'board' && <Layout size={14} style={{ marginRight: '2px' }} />}
              {tab === 'activity' && <Activity size={14} style={{ marginRight: '2px' }} />}
              {tab === 'leaderboard' && <Award size={14} style={{ marginRight: '2px' }} />}
              {tab.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Invite banner — shown when user is the only member */}
      {isSoleMember && activeSquad && (
        <InviteBanner code={activeSquad.join_code} squadName={activeSquad.name} />
      )}

      {/* Tab content */}
      <div style={{ flex: 1 }}>
        {activeTab === 'board' && (
          <div className="animate-fade-in">
            <div style={{ display: 'flex', gap: 'var(--sm)', alignItems: 'center', marginBottom: 'var(--md)' }}>
              <input
                type="text"
                className="input"
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                placeholder="Add team task..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!newTaskText.trim()) return
                    createSquadTask(newTaskText.trim())
                    setNewTaskText('')
                  }
                }}
                style={{ flex: 1 }}
              />
              <button
                onClick={() => {
                  if (!newTaskText.trim()) return
                  createSquadTask(newTaskText.trim())
                  setNewTaskText('')
                }}
                className="btn btn-primary btn-icon"
                style={{ width: '48px', height: '48px', flexShrink: 0 }}
              >
                <Plus size={20} />
              </button>
            </div>

            {squadTasks.length === 0 ? (
              <div className="empty-state">
                <Layout size={40} className="text-dim" />
                <p className="body text-dim">
                  No team tasks yet.<br />Add one above to get started.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sm)' }}>
                {squadTasks.map((item) => (
                  <div
                    key={item.id}
                    className="card card-mid animate-fade-slide-up"
                    style={{
                      padding: 'var(--md)',
                      display: 'flex',
                      alignItems: 'center',
                      borderLeft: item.priority === 1 ? '3px solid var(--accent)' : '3px solid transparent',
                      backgroundColor: item.priority === 1 ? 'color-mix(in srgb, var(--accent) 7%, var(--card-mid))' : 'var(--card-mid)',
                    }}
                  >
                    <button
                      onClick={() => completeSquadTask(item.id, !item.done)}
                      style={{
                        marginRight: 'var(--md)',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: `2px solid ${item.done ? 'var(--signal)' : 'var(--border)'}`,
                        backgroundColor: item.done ? 'var(--signal)' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      {item.done && <Check size={12} color="#000" />}
                    </button>
                    <div style={{ flex: 1 }}>
                      <span
                        className="body text-primary"
                        style={{
                          textDecoration: item.done ? 'line-through' : 'none',
                          color: item.done ? 'var(--text-dim)' : 'var(--text)',
                        }}
                      >
                        {item.text}
                      </span>
                      {item.ai_reason && !item.done && (
                        <p
                          className="label text-dim"
                          style={{
                            fontSize: '10px',
                            marginTop: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.ai_reason}
                        </p>
                      )}
                    </div>
                    {item.assigned_to && (
                      <div
                        className="lb-avatar"
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--card-high)',
                          fontSize: '10px',
                          marginLeft: 'var(--sm)',
                        }}
                        title={item.assigned_to}
                      >
                        {memberInitial(item.assigned_to)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lg)' }}>
            {members.length === 0 ? (
              <div className="empty-state">
                <Activity size={40} className="text-dim" />
                <p className="body text-dim">
                  No team members yet.<br />Invite someone to see their activity here.
                </p>
              </div>
            ) : (
              members.map((member) => {
                const focusTask = memberFocusTasks.find((ft) => ft.user_id === member.user_id)
                return (
                  <div
                    key={member.user_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: 'var(--sm) 0',
                    }}
                  >
                    <div
                      className="lb-avatar"
                      style={{
                        width: '48px',
                        height: '48px',
                        fontSize: '18px',
                        borderWidth: focusTask ? '2px' : '1px',
                        borderColor: focusTask ? 'var(--signal)' : 'var(--border)',
                      }}
                    >
                      {memberInitial(member.user_id)}
                    </div>
                    <div style={{ marginLeft: 'var(--md)', flex: 1 }}>
                      <h3 className="title text-primary" style={{ fontSize: '15px' }}>
                        {member.full_name || member.email?.split('@')[0] || 'Unknown'}
                      </h3>
                      <p
                        className="body-md"
                        style={{
                          color: focusTask ? 'var(--signal)' : 'var(--text-dim)',
                          marginTop: '2px',
                        }}
                      >
                        {focusTask ? `⚡ ${focusTask.text}` : 'Not in session'}
                      </p>
                    </div>
                    {focusTask && (
                      <div
                        className="animate-blink"
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--signal)',
                        }}
                      />
                    )}
                  </div>
                )
              })
            )}
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div className="animate-fade-in">
            {snrLeaderboard.length === 0 ? (
              <div className="empty-state">
                <Award size={48} className="text-dim" />
                <p className="body text-dim" style={{ marginTop: 'var(--md)' }}>
                  No stats for today yet.<br />Focus on some tasks and check back.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lg)' }}>
                {[...snrLeaderboard]
                  .sort((a, b) => b.snr_pct - a.snr_pct)
                  .map((item, index) => {
                    const isTopThree = index < 3
                    const rankEmoji = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null
                    const pct = item.snr_pct
                    const colorVar = pct >= 75 ? 'var(--signal)' : pct >= 50 ? 'var(--amber)' : 'var(--noise)'

                    return (
                      <div key={item.user_id} className="lb-row">
                        <span
                          className="title"
                          style={{
                            color: isTopThree ? 'var(--accent)' : 'var(--text-muted)',
                            width: '28px',
                            textAlign: 'center',
                          }}
                        >
                          {rankEmoji || index + 1}
                        </span>

                        <div className="lb-avatar">
                          {(item.display_name ?? '?').charAt(0).toUpperCase()}
                        </div>

                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span className="body text-primary">{item.display_name ?? 'Unknown'}</span>
                            <span className="title" style={{ color: colorVar }}>
                              {pct}%
                            </span>
                          </div>
                          <div className="progress-bar-track" style={{ height: '4px' }}>
                            <div
                              className="progress-bar-fill"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: colorVar,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
